<template>

  <v-container fluid class="app-sign-key-selector-main-container" :class="isMobileOnly ? 'pa-0' : 'pa-3'">

    <div v-if="showTitle" class="d-flex justify-space-between">
      <div class="text-subtitle-1 app-sign-key-selector-title" :class="active ? 'active' : ''">
        <span>{{$t(`appSign.keySelector.${PrivateKeyType[privateKeyType]}`)}}</span>
      </div>
      <div>
        <slot name="title-append"></slot>
      </div>
    </div>

    <div v-if="isValidSelectedTab">
      <v-chip-group v-model="selectedTab" class="app-sign-key-selector-chip-group" active-class="primary--text" mandatory show-arrows
        :key="chipGroupKey" @change="onTabChange">
        <v-chip :value="AppSignKeySelectorTab.FileSystem" v-show="isFileSystemTabVisible" :disabled="isFileSystemTabDisabled">
          {{$t('appSign.tabs.file')}}
        </v-chip>
        <v-chip :value="AppSignKeySelectorTab.Device" v-show="isDeviceTabVisible" :disabled="isDeviceTabDisabled">
          {{$t('appSign.tabs.device')}}
        </v-chip>
        <v-chip :value="AppSignKeySelectorTab.Cloud" v-show="isCloudTabVisible" :disabled="isCloudTabDisabled">
          {{$t('appSign.tabs.cloud')}}
        </v-chip>
      </v-chip-group>
    </div>

    <v-tabs-items v-model="selectedTab" class="mt-3">

      <v-tab-item>
        <app-select id="appSignAcskSelect" v-show="isAcskVisible && signSettings.isCertAuthSelectorVisible" v-model="selectCa"
          :items="certificatesAuthorities" return-object item-text="issuerCNs[0]" label="appSign.acck" :disabled="componentDisabled"
          @change="onCaChange">
          <template v-slot:label-append>
            <div class="d-flex">
              {{ $t('appSign.autoDetectAcckShort') }}
              <v-switch v-model="autoSelectCa" dense inset :ripple="false" hide-details class="ma-0 ml-2 pa-0" style="width: 42px"
                :disabled="componentDisabled" @change="onAutoSelectCaChanged">
              </v-switch>
            </div>
          </template>
        </app-select>

        <div v-if="isCertSelectorVisible" class="app-sign-certificates-selector-container">
          <app-sign-certificates-selector :componentDisabled="componentDisabled"></app-sign-certificates-selector>
        </div>

        <app-sign-file-tab v-if="isFileSystemTabRendered" :privateKeyType="privateKeyType" :certificateAuthority="selectCa"
          :eventBus="eventBus" :useUserCertificates="isCertSelectorVisible" :disabled="disabled"
          :should-validate-key-type="shouldValidateKeyType" @readed="onReaded" @ready="onReady" @not-ready="onNotReady">
        </app-sign-file-tab>
      </v-tab-item>

      <v-tab-item>
        <app-select id="appSignSealAcskSelect" v-show="isAcskVisible && signSettings.isCertAuthSelectorVisible" v-model="selectCa"
          :items="certificatesAuthorities" return-object item-text="issuerCNs[0]" label="appSign.acck" :disabled="componentDisabled"
          @change="onCaChange">
          <template v-slot:label-append>
            <div class="d-flex">
              {{ $t('appSign.autoDetectAcckShort') }}
              <v-switch v-model="autoSelectCa" dense inset :ripple="false" hide-details class="ma-0 ml-2 pa-0" style="width: 42px"
                :disabled="componentDisabled" @change="onAutoSelectCaChanged">
              </v-switch>
            </div>
          </template>
        </app-select>

        <div v-if="isCertSelectorVisible" class="app-sign-certificates-selector-container">
          <app-sign-certificates-selector :componentDisabled="componentDisabled"></app-sign-certificates-selector>
        </div>

        <app-sign-device-tab v-if="isDeviceTabRendered" :privateKeyType="privateKeyType" :certificateAuthority="selectCa"
          :eventBus="eventBus" :useUserCertificates="isCertSelectorVisible" :disabled="disabled"
          :should-validate-key-type="shouldValidateKeyType" @readed="onReaded" @ready="onReady" @not-ready="onNotReady">
        </app-sign-device-tab>
      </v-tab-item>

      <v-tab-item>
        <app-sign-cloud-tab v-if="isCloudTabRendered" :privateKeyType="privateKeyType" :certificateAuthority="selectCa" :eventBus="eventBus"
          :useUserCertificates="isCertSelectorVisible" :disabled="disabled" style="padding-top: 2px!important"
          :should-validate-key-type="shouldValidateKeyType" @readed="onReaded" @ready="onReady" @not-ready="onNotReady">
        </app-sign-cloud-tab>
      </v-tab-item>

    </v-tabs-items>

    <div v-if="messageText" v-html="messageText" :class="[ 'info-panel pa-2 mt-2', messageIsError ? 'error': 'success' ]">
    </div>

  </v-container>

</template>

<script>
import Vue from 'vue';
import intersection from 'lodash/intersection';

import AppSelect from '@/components/Common/AppSelect';
import { formatErrorToHtml } from '@/helpers/';
import uiMixin from '@/mixins/ui-mixin';
import { actions as _act } from '@/models/store.actions';

import AppSignFileTab from './AppSignFileTab';
import AppSignDeviceTab from './AppSignDeviceTab';
import AppSignCloudTab from './AppSignCloudTab';

import { euSignFilesService } from '@/sign/services/eusign-files.service';
import { PrivateKeyType, SignMethod, AppSignKeySelectorTab } from '@/sign/models/sign-constants';
import { signService } from '@/sign/services/sign.service';
import { SignSettings } from '@/sign/models/sign-settings.model';

import { logger } from '@/helpers/log.service';
import AppSignCertificatesSelector from '@/sign/components/AppSignCertificatesSelector';
import { AppError } from '@/models/log.models';

export default {
  name: 'AppSignKeySelector',
  components: {
    AppSelect,
    AppSignFileTab,
    AppSignDeviceTab,
    AppSignCloudTab,
    AppSignCertificatesSelector
  },
  mixins: [uiMixin],
  inject: ['isMobileOnly'],
  props: {
    eventBus: { default: () => new Vue() },
    disabled: { default: false },
    privateKeyType: { type: String, default: PrivateKeyType.Key },
    allowedMethods: { type: Array, default: () => undefined },
    active: { type: Boolean, default: false },
    showTitle: { type: Boolean, default: false },
    shouldValidateKeyType: { type: Boolean, default: false }
  },
  data() {
    return {
      certificatesAuthorities: [],
      keyIsReaded: false,
      messageIsError: false,
      messageText: '',
      renderedTabs: [],
      selectCa: null,
      selectedTab: -1,
      signMethodMap: [SignMethod.FileSystem, SignMethod.Device, SignMethod.Cloud],
      signSettings: new SignSettings(),
      isCertificateNotFoundErrorOcurred: false,
      isResetBtnVisible: true,
      chipGroupKey: 0,
      autoSelectCa: false
    };
  },
  computed: {
    componentDisabled() {
      return this.disabled || this.keyIsReaded;
    },
    isAcskVisible() {
      return this.selectedTab === AppSignKeySelectorTab.FileSystem || this.selectedTab === AppSignKeySelectorTab.Device;
    },
    isDeviceTabVisible() {
      return (
        this.signSettings.visibleTabs.includes(SignMethod.Device) &&
        (!this.allowedMethods || this.allowedMethods.includes(SignMethod.Device))
      );
    },
    isDeviceTabDisabled() {
      return this.componentDisabled || !this.signSettings.enabledTabs.includes(SignMethod.Device);
    },
    isDeviceTabRendered() {
      return this.renderedTabs.includes(SignMethod.Device);
    },
    isFileSystemTabDisabled() {
      return this.componentDisabled || !this.signSettings.enabledTabs.includes(SignMethod.FileSystem);
    },
    isFileSystemTabRendered() {
      return this.renderedTabs.includes(SignMethod.FileSystem);
    },
    isFileSystemTabVisible() {
      return (
        this.signSettings.visibleTabs.includes(SignMethod.FileSystem) &&
        (!this.allowedMethods || this.allowedMethods.includes(SignMethod.FileSystem))
      );
    },
    signMethod() {
      return this.signMethodMap[this.selectedTab];
    },
    isCloudTabDisabled() {
      return this.componentDisabled || !this.signSettings.enabledTabs.includes(SignMethod.Cloud);
    },
    isCloudTabRendered() {
      return this.renderedTabs.includes(SignMethod.Cloud);
    },
    isCloudTabVisible() {
      return (
        this.signSettings.visibleTabs.includes(SignMethod.Cloud) && (!this.allowedMethods || this.allowedMethods.includes(SignMethod.Cloud))
      );
    },
    isCertSelectorVisible() {
      return this.isAcskVisible && this.useUserSelectedCertificatesInReadKey;
    },
    useUserSelectedCertificatesInReadKey() {
      return this.isCertificateNotFoundErrorOcurred || this.userStoredCertificates.length > 0;
    },
    currentUserEmail() {
      return this.$store.getters[_act.AUTH_GET_EMAILUSERGET] || '';
    },
    userStoredCertificates() {
      return this.$store.getters[_act.SIGN_GET_USER_CERT](this.currentUserEmail);
    },
    isValidSelectedTab() {
      return this.selectedTab >= AppSignKeySelectorTab.FileSystem;
    },
    signingParamsRequestMsg() {
      return `SigningParams-Request-${this.privateKeyType}`;
    },
    signingParamsResponseMsg() {
      return `SigningParams-Response-${this.privateKeyType}`;
    },
    signingParamsRequestChildMsg() {
      return `${this.signingParamsRequestMsg}-${this.signMethod}`;
    },
    signingParamsResponseChildMsg() {
      return `${this.signingParamsResponseMsg}-${this.signMethod}`;
    },
    AppSignKeySelectorTab() {
      return AppSignKeySelectorTab;
    },
    PrivateKeyType() {
      return PrivateKeyType;
    },
    isSignKeyType() {
      return this.privateKeyType === PrivateKeyType.Key;
    }
  },
  mounted() {
    this.initComponent();
  },
  beforeDestroy() {
    this.eventBus.$emit('app-sign-destroy');
    this.unsubscribe();
  },
  methods: {
    subscribe() {
      this.eventBus.$on('app-sign-prepare', this.prepareComponent);
      this.eventBus.$on('app-sign-reset-private-key', this.reset);
      this.eventBus.$on('app-sign-read-key-certificate-not-found-error', this.onCertificateNotFoundError);
      this.eventBus.$on(this.signingParamsRequestMsg, this.onSigningParamsRequest);
      this.eventBus.$on('app-sign-ui-updated', this.onSignUiUpdated);
    },
    unsubscribe() {
      this.eventBus.$off('app-sign-prepare', this.prepareComponent);
      this.eventBus.$off('app-sign-reset-private-key', this.reset);
      this.eventBus.$off('app-sign-read-key-certificate-not-found-error', this.onCertificateNotFoundError);
      this.eventBus.$off(this.signingParamsRequestMsg, this.onSigningParamsRequest);
      this.eventBus.$off('app-sign-ui-updated', this.onSignUiUpdated);
    },
    async onSigningParamsRequest() {
      let prms = await this.getSigningParams();
      this.eventBus.$emit(this.signingParamsResponseMsg, prms);
    },
    getSigningParams() {
      return new Promise((resolve) => {
        const fn = (prms) => {
          this.eventBus.$off(this.signingParamsResponseChildMsg);
          resolve(prms);
        };
        this.eventBus.$on(this.signingParamsResponseChildMsg, fn);
        this.eventBus.$emit(this.signingParamsRequestChildMsg);
      });
    },

    async initComponent() {
      const selTab = this.getSelectedTabFromStore();
      this.selectedTab = selTab ? parseInt(selTab) : AppSignKeySelectorTab.FileSystem;

      let autoSelectCa = this.getAutoSelectCaFromStore();
      const selectCa = this.getSelectCaFromStore();
      if (!autoSelectCa && !selectCa) {
        autoSelectCa = true;
      }
      this.autoSelectCa = autoSelectCa;
      if (!this.autoSelectCa) {
        this.selectCa = selectCa;
      }

      await this.prepareComponent();

      this.subscribe();
    },
    async prepareComponent() {
      this.applySignSettings();
      if (this.isValidSelectedTab) {
        await new Promise((resolve) => {
          this.$nextTick(async() => {
            await this.onTabChange();
            resolve();
          });
        });
      }
    },
    applySignSettings() {
      this.signSettings = signService.getCurrentSettings();
      if (
        !this.signSettings.visibleTabs.includes(this.signMethod) ||
        !this.signSettings.enabledTabs.includes(this.signMethod) ||
        (this.allowedMethods && !this.allowedMethods.includes(this.signMethod))
      ) {
        let accessibleTabs;
        if (this.allowedMethods) {
          accessibleTabs = intersection(this.signSettings.visibleTabs, this.signSettings.enabledTabs, this.allowedMethods);
        } else {
          accessibleTabs = intersection(this.signSettings.visibleTabs, this.signSettings.enabledTabs);
        }
        let newSelectedTab = accessibleTabs.length ? this.signMethodMap.indexOf(accessibleTabs[0]) : -1;
        if (newSelectedTab !== this.selectedTab) {
          this.eventBus.$emit('app-sign-reset-private-key-from-child');
        }
        this.selectedTab = newSelectedTab;
      }
      // for re-rendering of the chip group component
      this.$nextTick(() => {
        this.chipGroupKey += 1;
      });
    },

    onCaChange() {
      this.saveSelectCaToStore();
      const autoSelectCa = !this.selectCa;
      if (autoSelectCa !== this.autoSelectCa) {
        this.autoSelectCa = autoSelectCa;
        this.onAutoSelectCaChanged(autoSelectCa);
      }
    },
    onCertificateNotFoundError() {
      this.isCertificateNotFoundErrorOcurred = true;
    },
    async onTabChange() {
      const signMethod = this.signMethod;
      try {
        this.saveSelectedTabToStore();

        if (!signMethod) {
          throw new Error('Sign method not found');
        }

        if (this.isAcskVisible && !this.certificatesAuthorities.length) {
          this.certificatesAuthorities = await euSignFilesService.getCertifyingAuthorities();
          if (!this.autoSelectCa && !!this.selectCa && !this.certificatesAuthorities.some(ca => ca.address === this.selectCa.address)) {
            this.selectCa = null;
            this.autoSelectCa = true;
          }
        }

        this.resetMessage();
        if (signMethod && !this.renderedTabs.includes(signMethod)) {
          this.renderedTabs.push(signMethod);
        }
        this.eventBus.$emit('app-sign-method-changed', { signMethod });
      } catch (error) {
        this.handleError(error, 'appSign.errorSignComponentInit');
      }
    },

    handleError(error, header) {
      if (!(error instanceof AppError)) {
        logger.error(error);
      }
      this.messageIsError = true;
      this.messageText = formatErrorToHtml(error, header);
    },

    reset() {
      this.keyIsReaded = false;
      this.resetMessage();
    },
    resetMessage() {
      this.messageText = '';
      this.messageIsError = false;
    },

    onReaded(e) {
      this.keyIsReaded = true;
      this.$emit('readed', e);
    },
    onReady(e) {
      this.$emit('ready', e);
    },
    onNotReady(e) {
      this.$emit('not-ready', e);
      this.keyIsReaded = false;
    },

    onAutoSelectCaChanged() {
      this.saveAutoSelectCaToStore();
      if (this.autoSelectCa) {
        this.selectCa = null;
      } else if (!this.selectCa) {
        this.selectCa = this.getSelectCaFromStore();
      }
    },
    getSelectedTabFromStore() {
      let tab = this.isSignKeyType ? this.$store.getters[_act.SIGN_GET_SELECTEDTAB] : this.$store.getters[_act.SIGN_GET_SELECTEDTABSEAL];
      if (tab === 3) {
        // after removing mobile id cloud tab get value: 2
        tab = AppSignKeySelectorTab.Cloud;
      }
      return tab;
    },
    saveSelectedTabToStore() {
      const storeTab = this.getSelectedTabFromStore();
      if (storeTab !== this.selectedTab) {
        if (this.isSignKeyType) {
          this.$store.commit(_act.SIGN_MUT_SELECTEDTAB, {
            selectedTab: this.selectedTab
          });
        } else {
          this.$store.commit(_act.SIGN_MUT_SELECTEDTABSEAL, {
            selectedTab: this.selectedTab
          });
        }
      }
    },
    getAutoSelectCaFromStore() {
      return this.isSignKeyType ? !!this.$store.getters[_act.SIGN_GET_AUTOSELECTCN] : !!this.$store.getters[_act.SIGN_GET_AUTOSELECTCNSEAL];
    },
    saveAutoSelectCaToStore() {
      if (this.isSignKeyType) {
        this.$store.commit(_act.SIGN_MUT_AUTOSELECTCN, { autoSelectCN: this.autoSelectCa });
      } else {
        this.$store.commit(_act.SIGN_MUT_AUTOSELECTCNSEAL, { autoSelectCN: this.autoSelectCa });
      }
    },
    getSelectCaFromStore() {
      return this.isSignKeyType ? this.$store.getters[_act.SIGN_GET_SELECTEDCN] : this.$store.getters[_act.SIGN_GET_SELECTEDCNSEAL];
    },
    saveSelectCaToStore() {
      if (this.selectCa) {
        if (this.isSignKeyType) {
          this.$store.commit(_act.SIGN_MUT_SELECTEDCN, { selectedCN: this.selectCa });
        } else {
          this.$store.commit(_act.SIGN_MUT_SELECTEDCNSEAL, { selectedCN: this.selectCa });
        }
      }
    },
    onSignUiUpdated() {
      this.chipGroupKey += 1;
    }
  }
};
</script>

<style lang="scss">
.app-sign-key-selector-main-container {
  overflow-x: hidden;
  .app-sign-key-selector-title {
    &.active {
      font-weight: bold;
      & > span {
        border-bottom: 2px solid var(--v-primary-base);
      }
    }
  }
  .app-sign-key-selector-chip-group {
    &.v-item-group.v-slide-group {
      .v-slide-group__prev,
      .v-slide-group__next {
        min-width: auto;
      }
    }
  }
  .app-sign-certificates-selector-container {
    margin-bottom: 18px;
  }
}
</style>
